import { CalendarCheck, PiggyBank, Plus, Settings2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate } from '../shared/format'
import { useAccounts } from './api'
import { Footer } from './components/AccountForms'
import { BankLogo } from './components/banks/BankLogo'
import { BankSelect } from './components/banks/BankSelect'
import { Section } from './components/FinanceBits'
import { CheckRow, Field, FormSection } from './components/FormBits'
import {
  CURRENCIES,
  DEPOSIT_MANAGE,
  DEPOSIT_SETTINGS,
  depositBody,
  fmtAmount,
  useAddWithholdingRate,
  useCloseDeposit,
  useDepositPreview,
  useDeposits,
  useOpenDeposit,
  useWithholdingRates,
  type Deposit,
  type DepositCloseInput,
  type DepositInput,
} from './fx'
import { toastError } from './ui'

const STATUS_TONE = { open: 'info', matured: 'success', broken: 'warning' } as const
const today = () => new Date().toISOString().slice(0, 10)
const plusDays = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Vadeli hesaplar: açılış (vadesiz hesaptan anapara), vade sonu faiz/stopaj, iade ya da yenileme; stopaj oranları. */
export function DepositsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const deposits = useDeposits()
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState<{ d: Deposit; broken: boolean } | null>(null)
  const canManage = can(DEPOSIT_MANAGE)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.deposits.title')}
        description={t('finance.deposits.subtitle')}
        actions={
          canManage ? (
            <Button onClick={() => setOpening(true)}>
              <Plus />
              {t('finance.deposits.open')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <Section title={t('finance.deposits.listTitle')} icon={PiggyBank} bodyClassName="p-0">
          {deposits.isError ? (
            <ErrorState error={deposits.error} onRetry={() => void deposits.refetch()} />
          ) : deposits.isLoading ? (
            <Skeleton className="m-4 h-40" />
          ) : !deposits.data?.length ? (
            <EmptyState icon={PiggyBank} title={t('finance.deposits.empty')} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>{t('finance.deposits.account')}</TH>
                    <TH className="text-right">{t('finance.deposits.principal')}</TH>
                    <TH className="text-right">{t('finance.deposits.rate')}</TH>
                    <TH>{t('finance.deposits.term')}</TH>
                    <TH className="text-right">{t('finance.deposits.net')}</TH>
                    <TH>{t('finance.deposits.status')}</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {deposits.data.map((d) => (
                    <TR key={d.id}>
                      <TD>
                        <span className="flex items-center gap-2 font-medium">
                          <BankLogo url={d.bankLogoUrl} name={d.bankName} />
                          {d.accountName}
                        </span>
                      </TD>
                      <TD className="text-right tabular-nums">{fmtAmount(d.principal, d.currency)}</TD>
                      <TD className="text-right tabular-nums">%{d.annualRate}</TD>
                      <TD className="text-xs whitespace-nowrap">
                        {fmtDate(d.startDate)} – {fmtDate(d.maturityDate)}
                        <span className="text-muted-foreground block">
                          {d.status === 'open'
                            ? d.daysLeft > 0
                              ? t('finance.deposits.daysLeft', { count: d.daysLeft, total: d.days })
                              : t('finance.deposits.due')
                            : t('finance.deposits.closedOn', { date: fmtDate(d.closedOn) })}
                        </span>
                      </TD>
                      <TD className="text-right tabular-nums">
                        {fmtAmount(d.netInterest ?? d.expectedNet, d.currency)}
                        <span className="text-muted-foreground block text-xs">
                          {t('finance.deposits.withholdingShort', { rate: d.withholdingRate, amount: fmtAmount(d.withholding ?? d.expectedTax, d.currency) })}
                        </span>
                      </TD>
                      <TD>
                        <Badge tone={STATUS_TONE[d.status]}>{t(`finance.deposits.statuses.${d.status}`)}</Badge>
                      </TD>
                      <TD className="text-right whitespace-nowrap">
                        {canManage && d.status === 'open' ? (
                          <span className="inline-flex gap-1">
                            <Button size="sm" variant={d.daysLeft <= 0 ? 'default' : 'outline'} onClick={() => setClosing({ d, broken: false })}>
                              <CalendarCheck />
                              {t('finance.deposits.mature')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setClosing({ d, broken: true })}>
                              {t('finance.deposits.break')}
                            </Button>
                          </span>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </Section>
        <WithholdingSection canEdit={can(DEPOSIT_SETTINGS)} />
      </div>
      {opening ? <OpenDepositModal onDone={() => setOpening(false)} /> : null}
      {closing ? <CloseDepositModal deposit={closing.d} broken={closing.broken} onDone={() => setClosing(null)} /> : null}
    </>
  )
}

function OpenDepositModal({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const open = useOpenDeposit()
  const accounts = (useAccounts().data ?? []).filter((a) => a.active && a.type === 'bank')
  const [v, setV] = useState<DepositInput>({
    sourceAccountId: '',
    bankId: '',
    name: '',
    currency: 'TRY',
    principal: '',
    annualRate: '',
    dayBasis: 365,
    startDate: today(),
    maturityDate: plusDays(32),
    withholdingRate: '',
    note: '',
  })
  const set = (p: Partial<DepositInput>) => setV((x) => ({ ...x, ...p }))
  const source = accounts.find((a) => a.id === v.sourceAccountId)
  const currency = source?.currency || v.currency
  const valid = Number(v.principal.replace(',', '.')) > 0 && v.maturityDate > v.startDate && /^\d+([.,]\d+)?$/.test(v.annualRate.trim())
  // Önizleme yazmayı bekler; gövde metin anahtarıyla izlenir (her tuşta istek atılmaz).
  const bodyKey = valid ? JSON.stringify(depositBody({ ...v, currency })) : ''
  const [debounced, setDebounced] = useState(bodyKey)
  useEffect(() => {
    const h = setTimeout(() => setDebounced(bodyKey), 400)
    return () => clearTimeout(h)
  }, [bodyKey])
  const preview = useDepositPreview(debounced ? (JSON.parse(debounced) as ReturnType<typeof depositBody>) : null)
  const p = preview.data
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(o) => !o && onDone()}
      dismissible={!open.isPending}
      icon={<PiggyBank />}
      title={t('finance.deposits.openTitle')}
      description={t('finance.deposits.openHint')}
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid) return
        open.mutate(
          { ...v, currency },
          {
            onSuccess: () => {
              toast.success(t('finance.deposits.opened'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={open.isPending} disabled={!valid} onCancel={onDone} />}
    >
      <div className="space-y-5">
        <FormSection>
          <Field id="dp-src" label={t('finance.deposits.source')} hint={t('finance.deposits.sourceHint')} full>
            <Select
              id="dp-src"
              value={v.sourceAccountId}
              onChange={(id) => set({ sourceAccountId: id })}
              options={[
                { value: '', label: t('finance.deposits.existing') },
                ...accounts.map((a) => ({ value: a.id, label: `${a.name} · ${fmtAmount(a.balance, a.currency)}` })),
              ]}
            />
          </Field>
          {!v.sourceAccountId ? (
            <>
              <Field id="dp-bank" label={t('finance.accounts.bank')}>
                <BankSelect id="dp-bank" value={v.bankId} onChange={(bankId) => set({ bankId })} />
              </Field>
              <Field id="dp-cur" label={t('finance.fx.currency')}>
                <Select id="dp-cur" value={v.currency} onChange={(c) => set({ currency: c })} options={CURRENCIES.map((c) => ({ value: c, label: t(`finance.fx.currencies.${c}`) }))} />
              </Field>
            </>
          ) : null}
          <Field id="dp-name" label={t('finance.deposits.name')} hint={t('finance.deposits.nameHint')}>
            <Input id="dp-name" maxLength={80} value={v.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field id="dp-principal" label={t('finance.deposits.principal')}>
            <Input id="dp-principal" inputMode="decimal" value={v.principal} onChange={(e) => set({ principal: e.target.value })} />
          </Field>
          <Field id="dp-rate" label={t('finance.deposits.annualRate')}>
            <Input id="dp-rate" inputMode="decimal" value={v.annualRate} onChange={(e) => set({ annualRate: e.target.value })} />
          </Field>
          <Field id="dp-basis" label={t('finance.deposits.dayBasis')}>
            <Select id="dp-basis" value={String(v.dayBasis)} onChange={(b) => set({ dayBasis: Number(b) })} options={[{ value: '365', label: '365' }, { value: '360', label: '360' }]} />
          </Field>
          <Field id="dp-start" label={t('finance.deposits.start')}>
            <DateField id="dp-start" value={v.startDate} onChange={(d) => set({ startDate: d })} clearable={false} />
          </Field>
          <Field id="dp-maturity" label={t('finance.deposits.maturity')}>
            <DateField id="dp-maturity" value={v.maturityDate} onChange={(d) => set({ maturityDate: d })} clearable={false} />
          </Field>
          <Field id="dp-wht" label={t('finance.deposits.withholding')} hint={t('finance.deposits.withholdingHint', { rate: p?.withholdingRate ?? '—' })}>
            <Input id="dp-wht" inputMode="decimal" placeholder={p?.withholdingRate} value={v.withholdingRate} onChange={(e) => set({ withholdingRate: e.target.value })} />
          </Field>
        </FormSection>
        {p ? (
          <dl className="bg-muted/30 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border p-3 text-sm sm:grid-cols-4">
            {[
              [t('finance.deposits.days'), String(p.days)],
              [t('finance.deposits.gross'), fmtAmount(p.gross, currency)],
              [t('finance.deposits.tax'), fmtAmount(p.tax, currency)],
              [t('finance.deposits.maturityAmount'), fmtAmount(p.maturityAmount, currency)],
            ].map(([k, val]) => (
              <div key={k}>
                <dt className="text-muted-foreground text-xs">{k}</dt>
                <dd className="font-medium tabular-nums">{val}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </Modal>
  )
}

function CloseDepositModal({ deposit: d, broken, onDone }: { deposit: Deposit; broken: boolean; onDone: () => void }) {
  const { t } = useTranslation()
  const close = useCloseDeposit()
  const [v, setV] = useState<DepositCloseInput>({
    date: broken ? today() : d.maturityDate,
    grossInterest: broken ? '0' : '',
    withholding: '',
    returnToSource: !!d.sourceAccountId,
    renew: false,
    renewRate: d.annualRate,
    renewMaturity: '',
  })
  const set = (p: Partial<DepositCloseInput>) => setV((x) => ({ ...x, ...p }))
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onDone()}
      dismissible={!close.isPending}
      icon={<CalendarCheck />}
      title={t(broken ? 'finance.deposits.breakTitle' : 'finance.deposits.matureTitle', { name: d.accountName })}
      description={t(broken ? 'finance.deposits.breakHint' : 'finance.deposits.matureHint', {
        gross: fmtAmount(d.expectedGross, d.currency),
        tax: fmtAmount(d.expectedTax, d.currency),
      })}
      onSubmit={(e) => {
        e.preventDefault()
        close.mutate(
          { id: d.id, broken, input: v },
          {
            onSuccess: () => {
              toast.success(t('finance.deposits.closed'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={close.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="dc-date" label={t('finance.deposits.closeDate')}>
          <DateField id="dc-date" value={v.date} onChange={(date) => set({ date })} clearable={false} />
        </Field>
        <Field id="dc-gross" label={t('finance.deposits.gross')} hint={t('finance.deposits.grossHint')}>
          <Input id="dc-gross" inputMode="decimal" placeholder={d.expectedGross} value={v.grossInterest} onChange={(e) => set({ grossInterest: e.target.value })} />
        </Field>
        <Field id="dc-tax" label={t('finance.deposits.tax')}>
          <Input id="dc-tax" inputMode="decimal" placeholder={d.expectedTax} value={v.withholding} onChange={(e) => set({ withholding: e.target.value })} />
        </Field>
        <div className="space-y-2 sm:col-span-2">
          {d.sourceAccountId ? (
            <CheckRow checked={v.returnToSource && !v.renew} disabled={v.renew} onChange={(e) => set({ returnToSource: e.target.checked })}>
              {t('finance.deposits.returnToSource')}
            </CheckRow>
          ) : null}
          {!broken ? (
            <CheckRow checked={v.renew} onChange={(e) => set({ renew: e.target.checked })}>
              {t('finance.deposits.renew')}
            </CheckRow>
          ) : null}
        </div>
        {v.renew ? (
          <>
            <Field id="dc-rrate" label={t('finance.deposits.annualRate')}>
              <Input id="dc-rrate" inputMode="decimal" value={v.renewRate} onChange={(e) => set({ renewRate: e.target.value })} />
            </Field>
            <Field id="dc-rmat" label={t('finance.deposits.maturity')} hint={t('finance.deposits.renewHint')}>
              <DateField id="dc-rmat" value={v.renewMaturity} onChange={(renewMaturity) => set({ renewMaturity })} />
            </Field>
          </>
        ) : null}
      </FormSection>
    </Modal>
  )
}

function WithholdingSection({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation()
  const rates = useWithholdingRates()
  const add = useAddWithholdingRate()
  const [form, setForm] = useState<null | { currencyGroup: string; maxMonths: string; rate: string; validFrom: string; note: string }>(null)
  return (
    <Section
      title={t('finance.deposits.withholdingTitle')}
      icon={Settings2}
      bodyClassName="p-0"
      actions={
        canEdit ? (
          <Button size="sm" variant="outline" onClick={() => setForm({ currencyGroup: 'TRY', maxMonths: '', rate: '', validFrom: today(), note: '' })}>
            <Plus />
            {t('finance.deposits.addRate')}
          </Button>
        ) : null
      }
    >
      <p className="text-muted-foreground border-b px-4 py-2 text-xs">{t('finance.deposits.withholdingNote')}</p>
      {rates.isLoading ? (
        <Skeleton className="m-4 h-24" />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('finance.deposits.group')}</TH>
              <TH>{t('finance.deposits.band')}</TH>
              <TH className="text-right">{t('finance.deposits.rate')}</TH>
              <TH>{t('finance.deposits.validFrom')}</TH>
              <TH>{t('finance.deposits.note')}</TH>
            </TR>
          </THead>
          <TBody>
            {(rates.data ?? []).map((r) => (
              <TR key={r.id}>
                <TD>{t(`finance.deposits.groups.${r.currencyGroup}`)}</TD>
                <TD>{r.maxMonths ? t('finance.deposits.upTo', { count: r.maxMonths }) : t('finance.deposits.longer')}</TD>
                <TD className="text-right tabular-nums">%{r.rate}</TD>
                <TD>{fmtDate(r.validFrom)}</TD>
                <TD className="text-muted-foreground text-xs">{r.note}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
      {form ? (
        <Modal
          open
          onOpenChange={(o) => !o && setForm(null)}
          dismissible={!add.isPending}
          icon={<Settings2 />}
          title={t('finance.deposits.addRate')}
          description={t('finance.deposits.addRateHint')}
          onSubmit={(e) => {
            e.preventDefault()
            add.mutate(form, {
              onSuccess: () => {
                toast.success(t('finance.deposits.rateSaved'))
                setForm(null)
              },
              onError: toastError,
            })
          }}
          footer={<Footer pending={add.isPending} disabled={!(Number(form.rate.replace(',', '.')) >= 0) || !form.rate} onCancel={() => setForm(null)} />}
        >
          <FormSection>
            <Field id="wr-group" label={t('finance.deposits.group')}>
              <Select id="wr-group" value={form.currencyGroup} onChange={(g) => setForm({ ...form, currencyGroup: g })} options={['TRY', 'FX', 'XAU'].map((g) => ({ value: g, label: t(`finance.deposits.groups.${g}`) }))} />
            </Field>
            <Field id="wr-band" label={t('finance.deposits.band')} hint={t('finance.deposits.bandHint')}>
              <Input id="wr-band" inputMode="numeric" value={form.maxMonths} onChange={(e) => setForm({ ...form, maxMonths: e.target.value })} />
            </Field>
            <Field id="wr-rate" label={t('finance.deposits.rate')}>
              <Input id="wr-rate" inputMode="decimal" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            </Field>
            <Field id="wr-from" label={t('finance.deposits.validFrom')}>
              <DateField id="wr-from" value={form.validFrom} onChange={(d) => setForm({ ...form, validFrom: d })} clearable={false} />
            </Field>
            <Field id="wr-note" label={t('finance.deposits.note')} full>
              <Input id="wr-note" maxLength={200} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </Field>
          </FormSection>
        </Modal>
      ) : null}
    </Section>
  )
}
